import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { DollarSign, TrendingUp, Building2, Filter, RefreshCw, User, Calendar, Eye, CheckCircle, Clock, Banknote, Wallet, Crown, Users, ArrowUpRight, AlertTriangle, Pencil, Check, X, FileText, Phone } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function PagoItem({ pago, formatearMonto }) {
  // Parsear fecha correctamente sin problemas de timezone
  let fechaStr = '';
  if (pago.fecha_programada) {
    const [year, month, day] = pago.fecha_programada.split('T')[0].split('-');
    const fecha = new Date(year, month - 1, day);
    fechaStr = fecha.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
  }
  // Usar la moneda del pago para formatear correctamente
  const monedaPago = pago.moneda || 'USD';
  return (
    <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <User className="w-8 h-8 text-amber-500 p-1 bg-amber-500/20 rounded-full" />
          <div>
            <p className="font-bold text-white">{pago.cliente_nombre}</p>
            <p className="text-xs text-zinc-400">Lote: {pago.lote_numero}</p>
            <p className="text-xs text-zinc-500">{fechaStr}</p>
          </div>
        </div>
        <div className="text-right">
          <p className="font-mono font-bold text-emerald-400">{formatearMonto(pago.monto_programado, monedaPago)}</p>
          <p className="text-xs text-zinc-500">Pago #{pago.numero_pago}</p>
        </div>
      </div>
    </div>
  );
}

function ModalPagosMes({ open, onClose, mes, formatearMonto, formatearTotalCombinado }) {
  if (!mes) return null;
  const pagosOriginales = mes.pagos || [];
  
  // Ordenar pagos por fecha (los primeros en pagar primero)
  const pagos = [...pagosOriginales].sort((a, b) => {
    const fechaA = a.fecha_programada ? new Date(a.fecha_programada) : new Date('9999-12-31');
    const fechaB = b.fecha_programada ? new Date(b.fecha_programada) : new Date('9999-12-31');
    return fechaA - fechaB;
  });
  
  const hayPagos = pagos.length > 0;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg bg-zinc-900 border-zinc-800 text-white max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <Calendar className="w-5 h-5 text-amber-500" />
            Pagos de {mes.mes_nombre}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {hayPagos ? pagos.map((pago, idx) => (
            <PagoItem key={idx} pago={pago} formatearMonto={formatearMonto} />
          )) : (
            <div className="text-center py-8 text-zinc-500">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No hay pagos programados</p>
            </div>
          )}
        </div>
        {hayPagos && (
          <div className="mt-4 p-3 bg-zinc-800 rounded-lg border border-amber-500/30">
            <div className="flex justify-between items-center">
              <span className="text-zinc-400">Total:</span>
              <span className="font-mono font-bold text-lg text-amber-400">{formatearTotalCombinado(mes.monto_usd || 0, mes.monto_mxn || 0)}</span>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function FlujoMensualItem({ mes, idx, onMesClick, formatearTotalCombinado }) {
  const isFirst = idx === 0;
  const bgClass = isFirst ? 'bg-emerald-500/10 border border-emerald-500/30' : 'bg-zinc-800';
  const textClass = isFirst ? 'text-emerald-500' : 'text-white';
  const count = mes.pagos_count || 0;
  
  return (
    <div 
      className={`text-center p-4 rounded-lg ${bgClass} cursor-pointer hover:ring-2 hover:ring-amber-500/50 transition-all`}
      onClick={() => onMesClick(mes)}
    >
      <p className="text-xs text-zinc-500 mb-2">{mes.mes_nombre}</p>
      <p className={`font-bold font-mono ${textClass}`}>{formatearTotalCombinado(mes.monto_usd || 0, mes.monto_mxn || 0)}</p>
      {count > 0 && <p className="text-xs text-zinc-400 mt-1">{count} pagos</p>}
    </div>
  );
}

function FlujoMensual({ stats, formatearTotalCombinado, onMesClick }) {
  if (!stats?.flujo_mensual_proyectado || stats.flujo_mensual_proyectado.length === 0) return null;
  const flujo = stats.flujo_mensual_proyectado;
  
  return (
    <section className="mb-8">
      <h2 className="text-zinc-400 text-lg font-semibold mb-4">Flujo Mensual Proyectado</h2>
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {flujo.map((mes, idx) => (
            <FlujoMensualItem key={mes.mes_nombre} mes={mes} idx={idx} onMesClick={onMesClick} formatearTotalCombinado={formatearTotalCombinado} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [fraccionamientos, setFraccionamientos] = useState([]);
  const [selectedFraccId, setSelectedFraccId] = useState(() => localStorage.getItem('filtro_fraccionamiento') || 'todos');
  const [loading, setLoading] = useState(true);
  const [monedaDisplay, setMonedaDisplay] = useState('USD'); // USD o MXN - TODO se muestra en esta moneda
  const [tipoCambio, setTipoCambio] = useState(17.50);
  const [editandoTC, setEditandoTC] = useState(false);
  const [tcTemporal, setTcTemporal] = useState('17.50');
  
  // Estado para modal de pagos del mes
  const [mesSeleccionado, setMesSeleccionado] = useState(null);
  const [pagosMesDialogOpen, setPagosMesDialogOpen] = useState(false);
  
  // Estado para modal de tratos
  const [tratosDialogOpen, setTratosDialogOpen] = useState(false);
  const [tratosDialogType, setTratosDialogType] = useState('todos'); // 'todos', 'deuda', 'pagados'
  const [tratosLista, setTratosLista] = useState([]);
  const [loadingTratos, setLoadingTratos] = useState(false);
  
  // Estado para estadísticas de pagos (igual que en Pagos.js)
  const [estadisticasPagos, setEstadisticasPagos] = useState({
    totales: { efectivo: 0, banco: 0 },
    conteo: { efectivo: 0, banco: 0 },
    total_general: 0,
    total_pagos: 0
  });
  
  // Estado para modal de pagos por método
  const [pagosPorMetodoDialog, setPagosPorMetodoDialog] = useState({ open: false, metodo: '', pagos: [] });
  
  // Estado para información del plan
  const [planInfo, setPlanInfo] = useState(null);
  
  // Estado para pagos realizados por mes
  const [pagosRealizadosPorMes, setPagosRealizadosPorMes] = useState([]);
  const [mesSeleccionadoPagosRealizados, setMesSeleccionadoPagosRealizados] = useState(null);
  const [pagosRealizadosDialogOpen, setPagosRealizadosDialogOpen] = useState(false);
  const [filtroPagosRealizados, setFiltroPagosRealizados] = useState('todos'); // 'todos', 'enganches', 'mensualidades'
  
  // Estado para modal de enganches
  const [enganchesDialogOpen, setEnganchesDialogOpen] = useState(false);
  const [enganchesData, setEnganchesData] = useState([]);
  
  // Estados para modals de Control Financiero
  const [ingresosDialogOpen, setIngresosDialogOpen] = useState(false);
  const [ingresosAnioFiltro, setIngresosAnioFiltro] = useState(new Date().getFullYear().toString());
  const [todosPagosRealizados, setTodosPagosRealizados] = useState([]); // Sin filtro de año
  const [porCobrarDialogOpen, setPorCobrarDialogOpen] = useState(false);
  const [proyeccionDialogOpen, setProyeccionDialogOpen] = useState(false);
  const [mesExpandido, setMesExpandido] = useState(null); // Estado para expandir mes en ingresos
  
  // Estado para Control de Pagarés
  const [pagaresData, setPagaresData] = useState({ entregados: [], pendientes: [], pendientes_por_cliente: [], entregados_count: 0, pendientes_count: 0, total_pagados: 0, fraccionamientos: [] });
  const [pagaresDialogOpen, setPagaresDialogOpen] = useState(false);
  const [pagaresFiltroPendiente, setPagaresFiltroPendiente] = useState('pendientes'); // 'pendientes' o 'entregados'
  const [pagaresFiltroFracc, setPagaresFiltroFracc] = useState('todos'); // Filtro por fraccionamiento
  
  // Estado para proyección de ingresos futuros
  const [proyeccionIngresos, setProyeccionIngresos] = useState([]);
  
  // Estado para tratos pagados completamente
  const [tratosPagados, setTratosPagados] = useState({ tratos: [], totales: {}, conteo: {} });
  
  // Estado para modal de tratos por moneda
  const [tratosMonedaDialogOpen, setTratosMonedaDialogOpen] = useState(false);
  const [tratosMonedaFiltro, setTratosMonedaFiltro] = useState('USD'); // 'USD' o 'MXN'
  const [tratosPorMoneda, setTratosPorMoneda] = useState([]);
  
  // Filtros para análisis de cobranza (cobrado)
  const [filtroAnio, setFiltroAnio] = useState(new Date().getFullYear().toString());
  const [filtroMoneda, setFiltroMoneda] = useState('todas'); // 'todas', 'USD', 'MXN'
  const [aniosDisponibles, setAniosDisponibles] = useState([]);
  
  // Filtro para proyección de ingresos
  const [filtroAnioProyeccion, setFiltroAnioProyeccion] = useState(new Date().getFullYear().toString());
  
  // Filtro de mes para Control de Cobros Recientes
  const [filtroMesCobros, setFiltroMesCobros] = useState(null);

  // Clases de tema - Siempre modo oscuro
  const t = {
    bg: 'bg-zinc-950',
    card: 'bg-zinc-900',
    border: 'border-zinc-800',
    text: 'text-white',
    textMuted: 'text-zinc-400',
    textSubtle: 'text-zinc-500',
    hover: 'hover:bg-zinc-800',
    input: 'bg-zinc-900 border-zinc-700 text-white',
    btnSecondary: 'bg-zinc-800 border-zinc-700 text-white',
  };

  const handleFraccFilter = (value) => {
    setSelectedFraccId(value);
    localStorage.setItem('filtro_fraccionamiento', value);
  };
  
  const handleMesClick = (mes) => {
    setMesSeleccionado(mes);
    setPagosMesDialogOpen(true);
  };

  // Función para abrir modal de tratos
  const handleVerTratos = async (tipo) => {
    setTratosDialogType(tipo);
    setTratosDialogOpen(true);
    setLoadingTratos(true);
    
    try {
      // 🔥 CORREGIDO: Incluir filtro de fraccionamiento
      const url = selectedFraccId === 'todos'
        ? `${BACKEND_URL}/api/tratos/resumen-estado`
        : `${BACKEND_URL}/api/tratos/resumen-estado?fraccionamiento_id=${selectedFraccId}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setTratosLista(data);
      }
    } catch (error) {
      toast.error('Error al cargar tratos');
    } finally {
      setLoadingTratos(false);
    }
  };

  // Función para ver pagos por método
  const handleVerPagosPorMetodo = async (metodo) => {
    try {
      const url = selectedFraccId === 'todos'
        ? `${BACKEND_URL}/api/pagos/por-metodo/${metodo}`
        : `${BACKEND_URL}/api/pagos/por-metodo/${metodo}?fraccionamiento_id=${selectedFraccId}`;
      const response = await fetch(url, { credentials: 'include' });
      if (response.ok) {
        const pagos = await response.json();
        setPagosPorMetodoDialog({ open: true, metodo, pagos });
      }
    } catch (error) {
      toast.error('Error al cargar pagos');
    }
  };

  // Función para ver tratos por moneda
  const handleVerTratosPorMoneda = async (moneda) => {
    try {
      setTratosMonedaFiltro(moneda);
      const baseUrl = selectedFraccId === 'todos'
        ? `${BACKEND_URL}/api/tratos/resumen-estado`
        : `${BACKEND_URL}/api/tratos/resumen-estado?fraccionamiento_id=${selectedFraccId}`;
      const response = await fetch(baseUrl, { credentials: 'include' });
      if (response.ok) {
        const tratos = await response.json();
        // Filtrar por moneda
        const tratosFiltrados = tratos.filter(t => (t.moneda || 'USD') === moneda);
        setTratosPorMoneda(tratosFiltrados);
        setTratosMonedaDialogOpen(true);
      }
    } catch (error) {
      toast.error('Error al cargar tratos');
    }
  };

  // Convertir CUALQUIER monto a la moneda seleccionada
  const convertirMonto = (monto, monedaOriginal = 'USD') => {
    if (!monto) return 0;
    // Si ya está en la moneda que queremos, no convertir
    if (monedaOriginal === monedaDisplay) return monto;
    
    // Convertir USD a MXN
    if (monedaOriginal === 'USD' && monedaDisplay === 'MXN') {
      return monto * tipoCambio;
    }
    // Convertir MXN a USD
    if (monedaOriginal === 'MXN' && monedaDisplay === 'USD') {
      return monto / tipoCambio;
    }
    return monto;
  };
  
  // Calcular total combinando USD y MXN según la moneda seleccionada
  const calcularTotal = (montoUSD, montoMXN) => {
    if (monedaDisplay === 'USD') {
      // Convertir MXN a USD y sumar
      return (montoUSD || 0) + ((montoMXN || 0) / tipoCambio);
    } else {
      // Convertir USD a MXN y sumar
      return ((montoUSD || 0) * tipoCambio) + (montoMXN || 0);
    }
  };
  
  // Formatear monto - SIEMPRE en la moneda seleccionada
  const formatearMonto = (monto, monedaOriginal = 'USD') => {
    const montoConvertido = convertirMonto(monto || 0, monedaOriginal);
    const simbolo = monedaDisplay === 'USD' ? 'USD $' : 'MXN $';
    return `${simbolo}${montoConvertido.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  };
  
  // Formatear total combinado (USD + MXN)
  const formatearTotalCombinado = (montoUSD, montoMXN) => {
    const total = calcularTotal(montoUSD, montoMXN);
    const simbolo = monedaDisplay === 'USD' ? 'USD $' : 'MXN $';
    return `${simbolo}${total.toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  };
  
  // Formatear monto con moneda específica (sin conversión) - para casos especiales
  const formatearMontoConMoneda = (monto, moneda) => {
    const simbolo = moneda === 'MXN' ? 'MXN $' : 'USD $';
    return `${simbolo}${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
  };

  // Cargar tipo de cambio de Google
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/tipo-cambio`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (data) {
          setTipoCambio(data.tipo_cambio);
          setTcTemporal(data.tipo_cambio.toFixed(2));
        }
      })
      .catch(() => {});
    
    // Cargar info del plan
    fetch(`${BACKEND_URL}/api/account/plan-info`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => data && setPlanInfo(data))
      .catch(() => {});
    
    // Cargar resumen de pagarés (depende del filtro de fraccionamiento)
    const urlPagares = selectedFraccId === 'todos'
      ? `${BACKEND_URL}/api/pagos/pagares/resumen`
      : `${BACKEND_URL}/api/pagos/pagares/resumen?fraccionamiento_id=${selectedFraccId}`;
    fetch(urlPagares, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => data && setPagaresData(data))
      .catch(() => {});
  }, [selectedFraccId]);

  // Guardar tipo de cambio editado
  const guardarTipoCambio = () => {
    const nuevoTC = parseFloat(tcTemporal);
    if (!isNaN(nuevoTC) && nuevoTC > 0) {
      setTipoCambio(nuevoTC);
      setEditandoTC(false);
      toast.success(`Tipo de cambio: $${nuevoTC.toFixed(2)} MXN/USD`);
    } else {
      toast.error('Tipo de cambio inválido');
    }
  };

  // Actualizar TC desde Google
  const actualizarTCGoogle = async () => {
    try {
      const r = await fetch(`${BACKEND_URL}/api/tipo-cambio`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setTipoCambio(data.tipo_cambio);
        setTcTemporal(data.tipo_cambio.toFixed(2));
        toast.success(`TC Google: $${data.tipo_cambio.toFixed(2)} MXN/USD`);
      }
    } catch { toast.error('Error al obtener TC'); }
  };

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/fraccionamientos`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(setFraccionamientos)
      .catch(() => {});
  }, []);

  // Cargar estadísticas de pagos cuando cambia el fraccionamiento
  useEffect(() => {
    const urlEstadisticas = selectedFraccId === 'todos'
      ? `${BACKEND_URL}/api/pagos/estadisticas`
      : `${BACKEND_URL}/api/pagos/estadisticas?fraccionamiento_id=${selectedFraccId}`;
    
    // Agregar filtro de mes si está seleccionado
    const separator = urlEstadisticas.includes('?') ? '&' : '?';
    const urlConFiltro = filtroMesCobros 
      ? `${urlEstadisticas}${separator}mes=${filtroMesCobros}&anio=${new Date().getFullYear()}`
      : urlEstadisticas;
    
    fetch(urlConFiltro, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => data && setEstadisticasPagos(data))
      .catch(() => {});
  }, [selectedFraccId, filtroMesCobros]);

  useEffect(() => {
    setLoading(true);
    const url = selectedFraccId === 'todos' 
      ? `${BACKEND_URL}/api/dashboard/stats`
      : `${BACKEND_URL}/api/dashboard/stats?fraccionamiento_id=${selectedFraccId}`;
    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setStats(data); })
      .catch(() => toast.error('Error al cargar'))
      .finally(() => setLoading(false));
  }, [selectedFraccId]);

  // Cargar filtros disponibles (años con datos)
  useEffect(() => {
    const urlFiltros = selectedFraccId === 'todos'
      ? `${BACKEND_URL}/api/pagos/filtros-disponibles`
      : `${BACKEND_URL}/api/pagos/filtros-disponibles?fraccionamiento_id=${selectedFraccId}`;
    
    fetch(urlFiltros, { credentials: 'include' })
      .then(r => r.ok ? r.json() : { anios: [] })
      .then(data => {
        // Siempre incluir el año actual en la lista
        const currentYear = new Date().getFullYear();
        const aniosConDatos = data.anios || [];
        
        // Combinar años con datos + año actual (sin duplicados)
        const todosAnios = [...new Set([currentYear, ...aniosConDatos])].sort((a, b) => b - a);
        setAniosDisponibles(todosAnios);
      })
      .catch(() => {
        // Si falla, al menos mostrar el año actual
        setAniosDisponibles([new Date().getFullYear()]);
      });
  }, [selectedFraccId]);

  // Cargar pagos realizados por mes y tratos pagados (usa filtroAnio)
  useEffect(() => {
    // URL base según filtro de fraccionamiento
    const baseParams = selectedFraccId === 'todos' ? '' : `fraccionamiento_id=${selectedFraccId}`;
    
    // Construir parámetros de filtro
    let filterParams = `anio=${filtroAnio}`;
    if (filtroMoneda !== 'todas') {
      filterParams += `&moneda=${filtroMoneda}`;
    }
    
    // URL para pagos realizados
    const urlPagosRealizados = `${BACKEND_URL}/api/pagos/realizados-por-mes?${baseParams ? baseParams + '&' : ''}${filterParams}`;
    
    // URL para tratos pagados completamente
    const urlTratosPagados = `${BACKEND_URL}/api/tratos/pagados-completamente?${baseParams ? baseParams + '&' : ''}${filterParams}`;
    
    // Cargar endpoints en paralelo
    Promise.all([
      fetch(urlPagosRealizados, { credentials: 'include' }).then(r => r.ok ? r.json() : []),
      fetch(urlTratosPagados, { credentials: 'include' }).then(r => r.ok ? r.json() : { tratos: [], totales: {}, conteo: {} })
    ]).then(([pagosData, tratosPagadosData]) => {
      setPagosRealizadosPorMes(pagosData);
      setTratosPagados(tratosPagadosData);
    }).catch(() => {});
  }, [selectedFraccId, filtroAnio, filtroMoneda]);

  // Cargar TODOS los pagos realizados (sin filtro de año) para modal de Ingresos
  useEffect(() => {
    if (!ingresosDialogOpen) return;
    const baseParams = selectedFraccId === 'todos' ? '' : `fraccionamiento_id=${selectedFraccId}`;
    const url = `${BACKEND_URL}/api/pagos/realizados-por-mes?${baseParams}`;
    fetch(url, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setTodosPagosRealizados(data))
      .catch(() => {});
  }, [ingresosDialogOpen, selectedFraccId]);

  // Cargar proyección de ingresos (usa filtroAnio del Control Financiero)
  useEffect(() => {
    const baseParams = selectedFraccId === 'todos' ? '' : `fraccionamiento_id=${selectedFraccId}`;
    let filterParams = `anio=${filtroAnio}`;
    if (filtroMoneda !== 'todas') {
      filterParams += `&moneda=${filtroMoneda}`;
    }
    
    const urlProyeccion = `${BACKEND_URL}/api/pagos/proyeccion-ingresos?${baseParams ? baseParams + '&' : ''}${filterParams}`;
    
    fetch(urlProyeccion, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(data => setProyeccionIngresos(data))
      .catch(() => {});
  }, [selectedFraccId, filtroAnio, filtroMoneda]);

  const handleVerPagosRealizadosMes = (mes) => {
    setMesSeleccionadoPagosRealizados(mes);
    setPagosRealizadosDialogOpen(true);
  };

  if (loading && !stats) {
    return (
      <div className={`min-h-screen ${t.bg} flex items-center justify-center`}>
        <div className="w-12 h-12 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${t.bg} p-4 sm:p-6 lg:p-8 overflow-y-auto`}>
      {/* Header con Transparencia */}
      <header className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <div>
            <h1 className={`text-2xl sm:text-3xl font-bold ${t.text}`}>Panel de Control</h1>
            <p className={t.textMuted}>{new Date().toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          </div>
          
          <div className="flex flex-wrap items-center gap-3">
            {/* Toggle Moneda */}
            {/* Selector de moneda - TODO se muestra en la moneda seleccionada */}
            <div className={`flex gap-1 ${t.card} border ${t.border} rounded-lg p-1`}>
              <button onClick={() => setMonedaDisplay('USD')} className={`px-4 py-2 rounded text-sm font-bold ${monedaDisplay === 'USD' ? 'bg-emerald-500 text-black' : t.textMuted}`}>Dólares</button>
              <button onClick={() => setMonedaDisplay('MXN')} className={`px-4 py-2 rounded text-sm font-bold ${monedaDisplay === 'MXN' ? 'bg-amber-500 text-black' : t.textMuted}`}>Pesos</button>
            </div>
            
            {/* Tipo de cambio editable */}
            <div className="flex items-center gap-2">
              {editandoTC ? (
                <div className="flex items-center gap-1">
                  <span className="text-xs text-zinc-400">TC:</span>
                  <input 
                    type="number" 
                    value={tcTemporal}
                    onChange={(e) => setTcTemporal(e.target.value)}
                    className="w-20 px-2 py-1 text-sm bg-zinc-800 border border-zinc-600 rounded text-white"
                    step="0.01"
                  />
                  <Button size="sm" onClick={guardarTipoCambio} className="h-7 px-2 bg-emerald-600 hover:bg-emerald-500">
                    <Check className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setEditandoTC(false)} className="h-7 px-2">
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div className="flex items-center gap-1">
                  <span className={`text-sm ${t.textSubtle}`}>TC: ${tipoCambio.toFixed(2)}</span>
                  <Button size="sm" variant="ghost" onClick={() => { setTcTemporal(tipoCambio.toFixed(2)); setEditandoTC(true); }} className="h-7 w-7 p-0" title="Editar TC">
                    <Pencil className="w-3 h-3" />
                  </Button>
                  <Button size="sm" variant="ghost" onClick={actualizarTCGoogle} className="h-7 w-7 p-0" title="Actualizar desde Google">
                    <RefreshCw className="w-3 h-3" />
                  </Button>
                </div>
              )}
            </div>
            
            <Select value={selectedFraccId} onValueChange={handleFraccFilter}>
              <SelectTrigger className={`w-[180px] ${t.input}`}><SelectValue placeholder="Filtrar..." /></SelectTrigger>
              <SelectContent className={`${t.card} border ${t.border}`}>
                <SelectItem value="todos">Todos</SelectItem>
                {fraccionamientos.map(f => <SelectItem key={f.fraccionamiento_id} value={f.fraccionamiento_id}>{f.nombre}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </header>

      {/* KPIs */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <div 
          className={`${t.card} border border-emerald-500/30 rounded-xl p-6 cursor-pointer hover:ring-2 hover:ring-emerald-500/50 transition-all`}
          onClick={() => handleVerTratos('todos')}
        >
          <div className="flex justify-between mb-4">
            <div><p className={t.textMuted}>Total Vendido</p><p className={`${t.textSubtle} text-sm`}>{stats?.total_tratos || 0} tratos</p></div>
            <div className="p-3 bg-emerald-500/10 rounded-xl"><Eye className="w-6 h-6 text-emerald-500" /></div>
          </div>
          <p className="text-2xl font-bold text-emerald-500 font-mono">{formatearTotalCombinado(stats?.ventas_usd_monto, stats?.ventas_mxn_monto)}</p>
          <p className="text-xs text-emerald-400 mt-2">Click para ver detalles →</p>
        </div>

        <div 
          className={`${t.card} border border-emerald-500/30 rounded-xl p-6 cursor-pointer hover:ring-2 hover:ring-emerald-500/50 transition-all`}
          onClick={() => handleVerTratos('pagados')}
        >
          <div className="flex justify-between mb-4">
            <div><p className={t.textMuted}>Pagados en Totalidad</p><p className={`${t.textSubtle} text-sm`}>{stats?.tratos_pagados_count || 0} tratos</p></div>
            <div className="p-3 bg-emerald-500/10 rounded-xl"><CheckCircle className="w-6 h-6 text-emerald-500" /></div>
          </div>
          <p className="text-2xl font-bold text-emerald-500 font-mono">{formatearTotalCombinado(stats?.tratos_pagados_usd, stats?.tratos_pagados_mxn)}</p>
          <p className="text-xs text-emerald-400 mt-2">Click para ver detalles →</p>
        </div>

        <div 
          className={`${t.card} border ${t.border} rounded-xl p-6 cursor-pointer hover:ring-2 hover:ring-emerald-500/50 transition-all`}
          onClick={async () => {
            try {
              // 🔥 CORREGIDO: Incluir filtro de fraccionamiento
              const url = selectedFraccId === 'todos'
                ? `${BACKEND_URL}/api/tratos/enganches-activos`
                : `${BACKEND_URL}/api/tratos/enganches-activos?fraccionamiento_id=${selectedFraccId}`;
              const response = await fetch(url, { credentials: 'include' });
              const enganches = await response.json();
              setEnganchesData(enganches);
              setEnganchesDialogOpen(true);
            } catch (error) {
              console.error('Error cargando enganches:', error);
            }
          }}
        >
          <div className="flex justify-between mb-4">
            <div><p className={t.textMuted}>Total Cobrado</p><p className={`${t.textSubtle} text-sm`}>Enganches</p></div>
            <div className="p-3 bg-emerald-500/10 rounded-xl"><TrendingUp className="w-6 h-6 text-emerald-500" /></div>
          </div>
          <p className="text-2xl font-bold text-emerald-400 font-mono">{formatearTotalCombinado(stats?.enganches_usd, stats?.enganches_mxn)}</p>
          <p className="text-xs text-zinc-500 mt-2">Click para ver detalles</p>
        </div>
      </section>

      {/* Deuda y Flujo */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div 
          className={`${t.card} border border-amber-500/30 rounded-xl p-8 cursor-pointer hover:ring-2 hover:ring-amber-500/50 transition-all`}
          onClick={() => handleVerTratos('deuda')}
        >
          <div className="flex justify-between mb-6">
            <div><p className={t.textMuted}>Deuda Pendiente</p><p className={`${t.textSubtle} text-sm`}>Por cobrar</p></div>
            <div className="p-4 bg-amber-500/10 rounded-xl"><Clock className="w-8 h-8 text-amber-500" /></div>
          </div>
          <p className="text-3xl font-bold text-amber-500 font-mono">{formatearTotalCombinado(stats?.deuda_usd, stats?.deuda_mxn)}</p>
          <p className="text-xs text-amber-400 mt-2">Click para ver tratos en deuda →</p>
        </div>

        <div className={`${t.card} border ${t.border} rounded-xl p-8`}>
          <div className="flex justify-between mb-4">
            <div><p className={t.textMuted}>Flujo Proyectado</p></div>
            <div className="p-4 bg-emerald-500/10 rounded-xl"><TrendingUp className="w-8 h-8 text-emerald-500" /></div>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className={`${t.textSubtle} text-sm`}>Este mes</span>
              <span className="text-2xl font-bold text-emerald-500 font-mono">{formatearTotalCombinado(stats?.flujo_mes_usd, stats?.flujo_mes_mxn)}</span>
            </div>
            <div className={`border-t ${t.border}`}></div>
            <div className="flex justify-between items-center">
              <span className={`${t.textSubtle} text-sm`}>Siguiente mes</span>
              <span className="text-2xl font-bold text-emerald-400 font-mono">{formatearTotalCombinado(stats?.flujo_siguiente_mes_usd, stats?.flujo_siguiente_mes_mxn)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* Métricas */}
      <section className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className={`${t.card} border ${t.border} rounded-xl p-4`}>
          <p className={`${t.textSubtle} text-sm mb-2`}>Próximos 7 días</p>
          <p className={`text-xl font-bold ${t.text} font-mono`}>{formatearTotalCombinado(stats?.proximos_7_dias_usd, stats?.proximos_7_dias_mxn)}</p>
          <p className={`${t.textSubtle} text-xs`}>{stats?.pagos_proximos_7_dias_count || 0} pagos</p>
        </div>
        <div className={`${t.card} border ${t.border} rounded-xl p-4 cursor-pointer ${t.hover}`} onClick={() => navigate('/tratos')}>
          <p className={`${t.textSubtle} text-sm mb-2`}>Tratos Financiados</p>
          <p className={`text-xl font-bold ${t.text} font-mono`}>{stats?.tratos_financiados_count || 0}</p>
        </div>
        <div className={`${t.card} border border-red-500/30 rounded-xl p-4`}>
          <p className="text-red-400 text-sm mb-2">Pagos Atrasados</p>
          <p className="text-xl font-bold text-red-500 font-mono">{stats?.pagos_atrasados_count || 0}</p>
        </div>
        <div className={`${t.card} border ${t.border} rounded-xl p-4`}>
          <p className={`${t.textSubtle} text-sm mb-2`}>Clientes en Riesgo</p>
          <p className={`text-xl font-bold ${t.text} font-mono`}>{stats?.clientes_en_riesgo || 0}</p>
        </div>
        <div className={`${t.card} border ${
          (stats?.comisiones_pendientes_total || 0) === 0 ? 'border-green-500/30' :
          (stats?.comisiones_pendientes_total || 0) > 10000 ? 'border-red-500/30' :
          'border-yellow-500/30'
        } rounded-xl p-4 cursor-pointer ${t.hover}`} onClick={() => navigate('/comisiones')} data-testid="comisiones-pendientes-card">
          <p className={`text-sm mb-2 ${
            (stats?.comisiones_pendientes_total || 0) === 0 ? 'text-green-400' :
            (stats?.comisiones_pendientes_total || 0) > 10000 ? 'text-red-400' :
            'text-yellow-400'
          }`}>Comisiones Pendientes</p>
          <p className={`text-xl font-bold font-mono ${
            (stats?.comisiones_pendientes_total || 0) === 0 ? 'text-green-500' :
            (stats?.comisiones_pendientes_total || 0) > 10000 ? 'text-red-500' :
            'text-yellow-500'
          }`}>${(stats?.comisiones_pendientes_total || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
          <p className={`${t.textSubtle} text-xs`}>{stats?.comisiones_pendientes_count || 0} comisiones</p>
        </div>
      </section>

      {/* Ventas por Moneda */}
      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <div 
          className={`${t.card} border border-emerald-500/30 rounded-xl p-6 cursor-pointer hover:ring-2 hover:ring-emerald-500/50 transition-all`}
          onClick={() => handleVerTratosPorMoneda('USD')}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">🇺🇸</span>
            <div>
              <p className={`font-bold ${t.text}`}>Ventas en USD</p>
              <p className={`${t.textSubtle} text-sm`}>{stats?.ventas_usd_count || 0} tratos · {stats?.ventas_usd_lotes || 0} lotes</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-emerald-500 font-mono">USD ${(stats?.ventas_usd_monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
          <p className="text-xs text-emerald-400 mt-2">Click para ver tratos →</p>
        </div>
        
        <div 
          className={`${t.card} border border-amber-500/30 rounded-xl p-6 cursor-pointer hover:ring-2 hover:ring-amber-500/50 transition-all`}
          onClick={() => handleVerTratosPorMoneda('MXN')}
        >
          <div className="flex items-center gap-3 mb-4">
            <span className="text-2xl">🇲🇽</span>
            <div>
              <p className={`font-bold ${t.text}`}>Ventas en MXN</p>
              <p className={`${t.textSubtle} text-sm`}>{stats?.ventas_mxn_count || 0} tratos · {stats?.ventas_mxn_lotes || 0} lotes</p>
            </div>
          </div>
          <p className="text-2xl font-bold text-amber-500 font-mono">MXN ${(stats?.ventas_mxn_monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
          <p className={`${t.textSubtle} text-xs mt-1`}>≈ USD ${(stats?.ventas_mxn_en_usd || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</p>
          <p className="text-xs text-amber-400 mt-2">Click para ver tratos →</p>
        </div>
      </section>

      {/* Flujo Mensual Proyectado */}
      <FlujoMensual stats={stats} formatearTotalCombinado={formatearTotalCombinado} onMesClick={handleMesClick} />

      {/* CONTROL FINANCIERO - Dashboard de Dirección */}
      <section className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <h2 className="text-xl font-bold text-white flex items-center gap-3">
            <div className="p-2 bg-gradient-to-br from-emerald-500/20 to-amber-500/20 rounded-lg">
              <TrendingUp className="w-6 h-6 text-white" />
            </div>
            Control Financiero
          </h2>
          
          {/* Filtro de Año */}
          <Select value={filtroAnio} onValueChange={setFiltroAnio}>
            <SelectTrigger className="w-32 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue placeholder="Año" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {aniosDisponibles.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {(() => {
          // =====================================================
          // CÁLCULOS DE CONTROL FINANCIERO CON MONEDAS SEPARADAS
          // =====================================================
          
          // Enganches cobrados - usar totales separados del backend
          const enganchesUSD = stats?.enganches_usd || 0;
          const enganchesMXN = stats?.enganches_mxn || 0;
          
          // Mensualidades cobradas - agregar por moneda desde pagosRealizadosPorMes
          let mensualidadesUSD = 0;
          let mensualidadesMXN = 0;
          pagosRealizadosPorMes.forEach(mes => {
            (mes.pagos || []).forEach(p => {
              const monto = p.monto_pagado || 0;
              const moneda = p.moneda || 'USD';
              if (moneda === 'MXN') {
                mensualidadesMXN += monto;
              } else {
                mensualidadesUSD += monto;
              }
            });
          });
          
          // Total cobrado = enganches + mensualidades (calculado con conversión según moneda seleccionada)
          const totalCobrado = calcularTotal(enganchesUSD + mensualidadesUSD, enganchesMXN + mensualidadesMXN);
          const totalEnganchesCobrados = calcularTotal(enganchesUSD, enganchesMXN);
          const totalMensualidadesCobradas = calcularTotal(mensualidadesUSD, mensualidadesMXN);
          
          // Pendiente por cobrar - usar totales separados del backend
          const totalPendiente = calcularTotal(stats?.pendiente_usd || 0, stats?.pendiente_mxn || 0);
          
          // Mora - pagos atrasados con moneda
          const pagosAtrasados = stats?.pagos_atrasados || [];
          let moraUSD = 0, moraMXN = 0;
          pagosAtrasados.forEach(p => {
            const moneda = p.moneda || 'USD';
            if (moneda === 'MXN') {
              moraMXN += p.monto || 0;
            } else {
              moraUSD += p.monto || 0;
            }
          });
          const montoEnMora = calcularTotal(moraUSD, moraMXN);
          const clientesAtrasados = [...new Set(pagosAtrasados.map(p => p.cliente_id))].length;
          
          // Próximos 7 días con moneda
          const pagosproximos = stats?.pagos_proximos_7_dias || [];
          let prox7USD = 0, prox7MXN = 0;
          pagosproximos.forEach(p => {
            const moneda = p.moneda || 'USD';
            if (moneda === 'MXN') {
              prox7MXN += p.monto || 0;
            } else {
              prox7USD += p.monto || 0;
            }
          });
          const montoProximo30Dias = calcularTotal(prox7USD, prox7MXN);
          
          // Proyección total del año
          const totalProyectado = totalCobrado + totalPendiente;
          
          // Eficiencia de cobranza
          const eficienciaCobranza = totalProyectado > 0 ? (totalCobrado / totalProyectado) * 100 : 0;
          
          // Indicador de riesgo
          const porcentajeMora = totalPendiente > 0 ? (montoEnMora / totalPendiente) * 100 : 0;
          const nivelRiesgo = porcentajeMora > 20 ? 'alto' : porcentajeMora > 10 ? 'medio' : 'bajo';
          const colorRiesgo = nivelRiesgo === 'alto' ? 'red' : nivelRiesgo === 'medio' ? 'amber' : 'emerald';
          
          // Helper para formatear montos calculados (ya están en la moneda correcta)
          const formatearCalculado = (monto) => {
            const simbolo = monedaDisplay === 'USD' ? 'USD $' : 'MXN $';
            return `${simbolo}${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
          };
          
          return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* BLOQUE 1: INGRESOS CONFIRMADOS - CLICKEABLE */}
              <div 
                className="bg-gradient-to-br from-emerald-900/30 to-emerald-950/50 border border-emerald-500/30 rounded-2xl p-6 cursor-pointer hover:border-emerald-400 hover:shadow-lg hover:shadow-emerald-500/10 transition-all"
                onClick={async () => {
                  // Cargar enganches antes de abrir el modal
                  try {
                    // 🔥 CORREGIDO: Incluir filtro de fraccionamiento
                    const url = selectedFraccId === 'todos'
                      ? `${BACKEND_URL}/api/tratos/enganches-activos`
                      : `${BACKEND_URL}/api/tratos/enganches-activos?fraccionamiento_id=${selectedFraccId}`;
                    const response = await fetch(url, { credentials: 'include' });
                    const data = await response.json();
                    setEnganchesData(data);
                  } catch (error) {
                    console.error('Error cargando enganches:', error);
                  }
                  setIngresosDialogOpen(true);
                }}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
                    <h3 className="text-emerald-400 font-semibold uppercase text-sm tracking-wide">Ingresos Confirmados</h3>
                  </div>
                  <Eye className="w-4 h-4 text-emerald-500/50" />
                </div>
                
                {/* Total Cobrado - Número Principal */}
                <p className="text-4xl font-bold text-emerald-400 font-mono mb-4">
                  {formatearCalculado(totalCobrado)}
                </p>
                
                {/* Desglose */}
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-zinc-400">Enganches cobrados</span>
                    <span className="text-emerald-400 font-mono">{formatearCalculado(totalEnganchesCobrados)}</span>
                  </div>
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-zinc-400">Mensualidades cobradas</span>
                    <span className="text-emerald-400 font-mono">{formatearCalculado(totalMensualidadesCobradas)}</span>
                  </div>
                </div>
                
                {/* Barra de progreso */}
                <div className="mt-4">
                  <div className="flex justify-between text-xs mb-1">
                    <span className="text-zinc-500">Eficiencia de cobranza</span>
                    <span className="text-emerald-400 font-bold">{eficienciaCobranza.toFixed(1)}%</span>
                  </div>
                  <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                    <div 
                      className="h-full bg-gradient-to-r from-emerald-600 to-emerald-400 rounded-full transition-all duration-500"
                      style={{ width: `${Math.min(eficienciaCobranza, 100)}%` }}
                    ></div>
                  </div>
                </div>
                <p className="text-xs text-emerald-500/50 mt-3 text-center">Click para ver detalle mensual</p>
              </div>
              
              {/* BLOQUE 2: POR COBRAR - CLICKEABLE */}
              <div 
                className="bg-gradient-to-br from-amber-900/30 to-amber-950/50 border border-amber-500/30 rounded-2xl p-6 cursor-pointer hover:border-amber-400 hover:shadow-lg hover:shadow-amber-500/10 transition-all"
                onClick={() => setPorCobrarDialogOpen(true)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-amber-500"></div>
                    <h3 className="text-amber-400 font-semibold uppercase text-sm tracking-wide">Por Cobrar</h3>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Indicador de Riesgo */}
                    <div className={`px-2 py-1 rounded-full text-xs font-bold ${
                      colorRiesgo === 'red' ? 'bg-red-500/20 text-red-400' :
                      colorRiesgo === 'amber' ? 'bg-amber-500/20 text-amber-400' :
                      'bg-emerald-500/20 text-emerald-400'
                    }`}>
                      {nivelRiesgo === 'alto' ? 'Alto' : nivelRiesgo === 'medio' ? 'Medio' : 'Bajo'}
                    </div>
                    <Eye className="w-4 h-4 text-amber-500/50" />
                  </div>
                </div>
                
                {/* Total Pendiente */}
                <p className="text-4xl font-bold text-amber-400 font-mono mb-4">
                  {formatearCalculado(totalPendiente)}
                </p>
                
                {/* Métricas de riesgo */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-2 bg-red-500/10 rounded-lg border border-red-500/20">
                    <div className="flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4 text-red-400" />
                      <span className="text-sm text-zinc-300">En mora</span>
                    </div>
                    <span className="text-red-400 font-mono font-bold">{formatearCalculado(montoEnMora)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-zinc-400">Clientes atrasados</span>
                    <span className={`font-bold ${clientesAtrasados > 0 ? 'text-red-400' : 'text-zinc-500'}`}>
                      {clientesAtrasados}
                    </span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-zinc-400">Próximos 7 días</span>
                    <span className="text-amber-400 font-mono">{formatearCalculado(montoProximo30Dias)}</span>
                  </div>
                </div>
                <p className="text-xs text-amber-500/50 mt-3 text-center">Click para ver pagos pendientes</p>
              </div>
              
              {/* BLOQUE 3: PROYECCIÓN - CLICKEABLE */}
              <div 
                className="bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-zinc-600/50 rounded-2xl p-6 cursor-pointer hover:border-zinc-400 hover:shadow-lg hover:shadow-zinc-500/10 transition-all"
                onClick={() => setProyeccionDialogOpen(true)}
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-zinc-400"></div>
                    <h3 className="text-zinc-300 font-semibold uppercase text-sm tracking-wide">Proyección {filtroAnio}</h3>
                  </div>
                  <Eye className="w-4 h-4 text-zinc-500/50" />
                </div>
                
                {/* Total Proyectado = Cobrado + Pendiente */}
                <p className="text-4xl font-bold text-white font-mono mb-4">
                  {formatearCalculado(totalCobrado + calcularTotal(
                    proyeccionIngresos.reduce((s, m) => s + ((m.monto_enganches_usd || 0) + (m.monto_mensualidades_usd || 0)), 0),
                    proyeccionIngresos.reduce((s, m) => s + ((m.monto_enganches_mxn || 0) + (m.monto_mensualidades_mxn || 0)), 0)
                  ))}
                </p>
                
                {/* Desglose: Confirmado + Pendiente */}
                <div className="space-y-3">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-amber-400">Enganches cobrados</span>
                    <span className="text-amber-400 font-mono">{formatearCalculado(totalEnganchesCobrados)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-emerald-400">Mensualidades cobradas</span>
                    <span className="text-emerald-400 font-mono">{formatearCalculado(totalMensualidadesCobradas)}</span>
                  </div>
                  
                  <div className="flex justify-between items-center text-sm border-t border-zinc-700 pt-2">
                    <span className="text-zinc-400">Pendiente por cobrar</span>
                    <span className="text-zinc-300 font-mono">{formatearCalculado(calcularTotal(
                      proyeccionIngresos.reduce((s, m) => s + ((m.monto_enganches_usd || 0) + (m.monto_mensualidades_usd || 0)), 0),
                      proyeccionIngresos.reduce((s, m) => s + ((m.monto_enganches_mxn || 0) + (m.monto_mensualidades_mxn || 0)), 0)
                    ))}</span>
                  </div>
                </div>
                <p className="text-xs text-zinc-500/50 mt-3 text-center">Click para ver detalle mensual</p>
              </div>
            </div>
          );
        })()}
      </section>

      {/* Realizadas por Mes - Al final del dashboard */}
      <section className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <h2 className={`${t.textMuted} text-lg font-semibold flex items-center gap-2`}>
            <div className="w-3 h-3 rounded-full bg-violet-500"></div>
            Pagos Realizados por Mes
          </h2>
          
          {/* Filtro de Año */}
          <Select value={filtroAnio} onValueChange={setFiltroAnio}>
            <SelectTrigger className="w-28 bg-zinc-800 border-zinc-700 text-white">
              <SelectValue placeholder="Año" />
            </SelectTrigger>
            <SelectContent className="bg-zinc-800 border-zinc-700">
              {aniosDisponibles.map(year => (
                <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className={`${t.card} border border-violet-500/30 rounded-xl p-6`}>
          {(() => {
            const mensualidadesPorMes = [...pagosRealizadosPorMes]
              .reverse()
              .map(mes => {
                const pagosDelMes = mes.pagos || [];
                // Calcular totales por moneda
                let totalUSD = 0, totalMXN = 0;
                pagosDelMes.forEach(p => {
                  const monto = p.monto_pagado || p.monto_programado || 0;
                  const moneda = p.moneda || p.moneda_trato || 'USD';
                  if (moneda === 'MXN') totalMXN += monto;
                  else totalUSD += monto;
                });
                const pagosEfectivo = pagosDelMes.filter(p => (p.metodo_pago || 'efectivo').toLowerCase() === 'efectivo').length;
                const pagosBanco = pagosDelMes.length - pagosEfectivo;
                return {
                  ...mes,
                  pagos: pagosDelMes,
                  total_pagos: pagosDelMes.length,
                  monto_usd: totalUSD,
                  monto_mxn: totalMXN,
                  pagos_efectivo: pagosEfectivo,
                  pagos_banco: pagosBanco
                };
              }).filter(mes => mes.total_pagos > 0);
            
            return mensualidadesPorMes.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
                {mensualidadesPorMes.map((mes, idx) => (
                  <div
                    key={mes.mes}
                    onClick={() => handleVerPagosRealizadosMes(mes)}
                    className={`p-4 rounded-xl border cursor-pointer transition-all hover:scale-105 ${
                      idx === mensualidadesPorMes.length - 1 
                        ? 'bg-violet-500/10 border-violet-500/50 hover:border-violet-500'
                        : 'bg-zinc-800 border-zinc-700 hover:border-violet-500/50'
                    }`}
                  >
                    <p className={`text-sm font-medium ${idx === mensualidadesPorMes.length - 1 ? 'text-violet-400' : t.textMuted}`}>
                      {mes.mes_nombre}
                    </p>
                    <p className={`text-2xl font-bold font-mono ${idx === mensualidadesPorMes.length - 1 ? 'text-violet-500' : 'text-violet-400'}`}>
                      {mes.total_pagos}
                    </p>
                    <p className={`text-xs ${t.textSubtle}`}>pagos</p>
                    <div className="flex gap-2 mt-1 text-xs">
                      {mes.pagos_efectivo > 0 && (
                        <span className="text-amber-400" title="Efectivo">💵 {mes.pagos_efectivo}</span>
                      )}
                      {mes.pagos_banco > 0 && (
                        <span className="text-violet-400" title="Banco">🏦 {mes.pagos_banco}</span>
                      )}
                    </div>
                    <p className={`text-sm font-mono mt-1 ${idx === mensualidadesPorMes.length - 1 ? 'text-violet-400' : 'text-zinc-400'}`}>
                      {formatearTotalCombinado(mes.monto_usd, mes.monto_mxn)}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6">
                <Calendar className={`w-10 h-10 mx-auto mb-2 text-violet-500/30`} />
                <p className={t.textMuted}>No hay pagos realizados en {filtroAnio}</p>
              </div>
            );
          })()}
        </div>
      </section>

      {/* Sección de Control de Pagarés */}
      <section className="mb-8">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4">
          <h2 className={`${t.textMuted} text-lg font-semibold flex items-center gap-2`}>
            <div className="w-3 h-3 rounded-full bg-orange-500"></div>
            Control de Pagarés
          </h2>
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => setPagaresDialogOpen(true)}
            className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
          >
            <FileText className="w-4 h-4 mr-2" />
            Ver detalle
          </Button>
        </div>
        <div 
          className={`${t.card} border border-orange-500/30 rounded-xl p-6 cursor-pointer hover:border-orange-500/50 transition-all`}
          onClick={() => setPagaresDialogOpen(true)}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Pagos Realizados */}
            <div className="text-center p-4 bg-zinc-800/50 rounded-xl">
              <p className="text-zinc-400 text-sm mb-1">Total Pagos Realizados</p>
              <p className="text-3xl font-bold text-white font-mono">{pagaresData.total_pagados || 0}</p>
              <p className="text-xs text-zinc-500">pagos en el sistema</p>
            </div>
            
            {/* Pagarés Entregados */}
            <div className="text-center p-4 bg-emerald-500/10 rounded-xl border border-emerald-500/30">
              <p className="text-emerald-400 text-sm mb-1 flex items-center justify-center gap-2">
                <CheckCircle className="w-4 h-4" />
                Pagarés Entregados
              </p>
              <p className="text-3xl font-bold text-emerald-400 font-mono">{pagaresData.entregados_count || 0}</p>
              <p className="text-xs text-emerald-400/70">✓ Ya entregados al cliente</p>
            </div>
            
            {/* Pagarés Pendientes */}
            <div className={`text-center p-4 rounded-xl border ${(pagaresData.pendientes_count || 0) > 0 ? 'bg-orange-500/10 border-orange-500/50' : 'bg-zinc-800/50 border-zinc-700'}`}>
              <p className={`text-sm mb-1 flex items-center justify-center gap-2 ${(pagaresData.pendientes_count || 0) > 0 ? 'text-orange-400' : 'text-zinc-400'}`}>
                <Clock className="w-4 h-4" />
                Pagarés Pendientes
              </p>
              <p className={`text-3xl font-bold font-mono ${(pagaresData.pendientes_count || 0) > 0 ? 'text-orange-400' : 'text-zinc-400'}`}>
                {pagaresData.pendientes_count || 0}
              </p>
              <p className={`text-xs ${(pagaresData.pendientes_count || 0) > 0 ? 'text-orange-400/70' : 'text-zinc-500'}`}>
                {(pagaresData.pendientes_count || 0) > 0 ? '⚠ Faltan por entregar' : 'Todo al día'}
              </p>
            </div>
          </div>
          
          {/* Lista rápida de clientes con pagarés pendientes */}
          {(pagaresData.pendientes_por_cliente || []).length > 0 && (
            <div className="mt-6 pt-4 border-t border-zinc-700">
              <p className="text-sm text-orange-400 mb-3 flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Clientes con pagarés pendientes:
              </p>
              <div className="flex flex-wrap gap-2">
                {pagaresData.pendientes_por_cliente.slice(0, 8).map((cliente, idx) => (
                  <span key={idx} className="px-3 py-1 bg-orange-500/20 text-orange-400 rounded-full text-sm">
                    {cliente.cliente_nombre} ({cliente.pagares?.length || 0})
                  </span>
                ))}
                {pagaresData.pendientes_por_cliente.length > 8 && (
                  <span className="px-3 py-1 bg-zinc-700 text-zinc-400 rounded-full text-sm">
                    +{pagaresData.pendientes_por_cliente.length - 8} más
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Acciones */}
      <section className="flex flex-wrap gap-3">
        <button onClick={() => navigate('/tratos')} className="bg-amber-500 text-black font-bold px-6 py-3 rounded-xl hover:bg-amber-400">+ Nuevo Trato</button>
        <button onClick={() => navigate('/pagos')} className={`${t.btnSecondary} border px-6 py-3 rounded-xl hover:border-amber-500/50`}>Gestionar Pagos</button>
      </section>
      
      <ModalPagosMes open={pagosMesDialogOpen} onClose={setPagosMesDialogOpen} mes={mesSeleccionado} formatearMonto={formatearMonto} formatearTotalCombinado={formatearTotalCombinado} />
      
      {/* Modal de Pagos Realizados por Mes */}
      <Dialog open={pagosRealizadosDialogOpen} onOpenChange={setPagosRealizadosDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-zinc-900 border-zinc-800 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-emerald-500" />
              Pagos de {mesSeleccionadoPagosRealizados?.mes_nombre}
            </DialogTitle>
          </DialogHeader>
          
          {/* Filtros por método de pago */}
          {mesSeleccionadoPagosRealizados?.pagos?.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-4">
              <Button 
                size="sm" 
                variant={filtroPagosRealizados === 'todos' ? 'default' : 'outline'}
                onClick={() => setFiltroPagosRealizados('todos')}
                className={filtroPagosRealizados === 'todos' ? 'bg-violet-600' : 'border-zinc-700 text-zinc-400'}
              >
                Todos ({mesSeleccionadoPagosRealizados?.pagos?.length || 0})
              </Button>
              <Button 
                size="sm" 
                variant={filtroPagosRealizados === 'efectivo' ? 'default' : 'outline'}
                onClick={() => setFiltroPagosRealizados('efectivo')}
                className={filtroPagosRealizados === 'efectivo' ? 'bg-amber-600' : 'border-zinc-700 text-zinc-400'}
              >
                💵 Efectivo ({mesSeleccionadoPagosRealizados?.pagos?.filter(p => (p.metodo_pago || 'efectivo').toLowerCase() === 'efectivo').length || 0})
              </Button>
              <Button 
                size="sm" 
                variant={filtroPagosRealizados === 'banco' ? 'default' : 'outline'}
                onClick={() => setFiltroPagosRealizados('banco')}
                className={filtroPagosRealizados === 'banco' ? 'bg-blue-600' : 'border-zinc-700 text-zinc-400'}
              >
                🏦 Banco ({mesSeleccionadoPagosRealizados?.pagos?.filter(p => (p.metodo_pago || '').toLowerCase() !== 'efectivo' && (p.metodo_pago || '').toLowerCase() !== '').length || 0})
              </Button>
            </div>
          )}
          
          <div className="space-y-3">
            {(() => {
              const pagos = mesSeleccionadoPagosRealizados?.pagos || [];
              const pagosFiltrados = pagos.filter(p => {
                if (filtroPagosRealizados === 'todos') return true;
                const metodo = (p.metodo_pago || 'efectivo').toLowerCase();
                if (filtroPagosRealizados === 'efectivo') return metodo === 'efectivo';
                if (filtroPagosRealizados === 'banco') return metodo !== 'efectivo' && metodo !== '';
                return true;
              });
              
              return pagosFiltrados.length > 0 ? (
                pagosFiltrados.map((pago, idx) => {
                  const esEnganche = pago.numero_pago === 0;
                  const monedaPago = pago.moneda || pago.moneda_trato || 'USD';
                  const metodoPago = (pago.metodo_pago || 'efectivo').toLowerCase();
                  const esEfectivo = metodoPago === 'efectivo';
                  
                  return (
                    <div key={idx} className="p-4 bg-zinc-800 rounded-lg border border-zinc-700">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 flex items-center justify-center rounded-full ${esEfectivo ? 'bg-amber-500/20' : 'bg-blue-500/20'}`}>
                            <span className="text-lg">{esEfectivo ? '💵' : '🏦'}</span>
                          </div>
                          <div>
                            <p className="font-bold text-white">{pago.cliente_nombre || 'Cliente'}</p>
                            <p className="text-sm text-zinc-400">Lote: {pago.lote_numero || '-'}</p>
                            <p className="text-xs text-zinc-500">
                              {pago.fecha_pago ? new Date(pago.fecha_pago).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : '-'}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono font-bold text-lg ${esEnganche ? 'text-blue-400' : 'text-emerald-400'}`}>
                            {formatearMonto(pago.monto_pagado || pago.monto_programado, monedaPago)}
                          </p>
                          <Badge className={`mt-1 ${esEfectivo ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                            {esEfectivo ? '💵 EFECTIVO' : '🏦 BANCO'}
                          </Badge>
                          <p className="text-xs text-zinc-500 mt-1">
                            {esEnganche ? 'ENGANCHE' : `MENSUALIDAD #${pago.numero_pago}`}
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="text-center py-8 text-zinc-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay pagos {filtroPagosRealizados !== 'todos' ? `con ${filtroPagosRealizados}` : 'realizados'} este mes</p>
                </div>
              );
            })()}
          </div>
          {mesSeleccionadoPagosRealizados?.pagos?.length > 0 && (() => {
            // Calcular totales por moneda y método
            let totalUSD = 0, totalMXN = 0, efectivoUSD = 0, efectivoMXN = 0, bancoUSD = 0, bancoMXN = 0;
            (mesSeleccionadoPagosRealizados.pagos || []).forEach(p => {
              const m = p.moneda || p.moneda_trato || 'USD';
              const monto = p.monto_pagado || p.monto_programado || 0;
              const metodo = (p.metodo_pago || 'efectivo').toLowerCase();
              if (m === 'MXN') {
                totalMXN += monto;
                if (metodo === 'efectivo') efectivoMXN += monto;
                else bancoMXN += monto;
              } else {
                totalUSD += monto;
                if (metodo === 'efectivo') efectivoUSD += monto;
                else bancoUSD += monto;
              }
            });
            return (
              <div className="mt-4 p-4 bg-zinc-800 rounded-lg border border-violet-500/30 space-y-2">
                <div className="flex justify-between items-center">
                  <span className="text-zinc-400">Total del mes:</span>
                  <span className="font-mono font-bold text-xl text-violet-400">{formatearTotalCombinado(totalUSD, totalMXN)}</span>
                </div>
                <div className="grid grid-cols-2 gap-4 pt-2 border-t border-zinc-700">
                  <div className="flex justify-between items-center">
                    <span className="text-amber-400 text-sm">💵 Efectivo:</span>
                    <span className="font-mono text-amber-400">{formatearTotalCombinado(efectivoUSD, efectivoMXN)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-blue-400 text-sm">🏦 Banco:</span>
                    <span className="font-mono text-blue-400">{formatearTotalCombinado(bancoUSD, bancoMXN)}</span>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>
      
      {/* Modal de Tratos */}
      <Dialog open={tratosDialogOpen} onOpenChange={setTratosDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-zinc-900 border-zinc-800 text-white max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              {tratosDialogType === 'deuda' ? (
                <><Clock className="w-5 h-5 text-amber-500" /> Tratos en Deuda</>
              ) : tratosDialogType === 'pagados' ? (
                <><CheckCircle className="w-5 h-5 text-emerald-500" /> Pagados en Totalidad</>
              ) : (
                <><Eye className="w-5 h-5 text-emerald-500" /> Todos los Tratos</>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-4">
            <Button 
              size="sm" 
              variant={tratosDialogType === 'todos' ? 'default' : 'outline'}
              onClick={() => setTratosDialogType('todos')}
              className={tratosDialogType === 'todos' ? 'bg-emerald-600' : 'border-zinc-700 text-zinc-400'}
            >
              Todos ({tratosLista.length})
            </Button>
            <Button 
              size="sm" 
              variant={tratosDialogType === 'deuda' ? 'default' : 'outline'}
              onClick={() => setTratosDialogType('deuda')}
              className={tratosDialogType === 'deuda' ? 'bg-amber-600' : 'border-zinc-700 text-zinc-400'}
            >
              En Deuda ({tratosLista.filter(t => t.saldo_pendiente > 0).length})
            </Button>
            <Button 
              size="sm" 
              variant={tratosDialogType === 'pagados' ? 'default' : 'outline'}
              onClick={() => setTratosDialogType('pagados')}
              className={tratosDialogType === 'pagados' ? 'bg-emerald-600' : 'border-zinc-700 text-zinc-400'}
            >
              Pagados en Totalidad ({tratosLista.filter(t => t.saldo_pendiente <= 0).length})
            </Button>
          </div>
          
          {/* Lista de tratos */}
          <div className="flex-1 overflow-y-auto space-y-3">
            {loadingTratos ? (
              <div className="text-center py-8 text-zinc-500">Cargando...</div>
            ) : (
              tratosLista
                .filter(trato => {
                  if (tratosDialogType === 'deuda') return trato.saldo_pendiente > 0;
                  if (tratosDialogType === 'pagados') return trato.saldo_pendiente <= 0;
                  return true;
                })
                .map((trato) => {
                  const formatFecha = (fecha) => {
                    if (!fecha) return '';
                    try {
                      return new Date(fecha).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
                    } catch { return ''; }
                  };
                  
                  return (
                    <div 
                      key={trato.trato_id} 
                      className={`p-4 rounded-lg border ${
                        trato.saldo_pendiente > 0 
                          ? 'bg-amber-500/5 border-amber-500/30' 
                          : 'bg-emerald-500/5 border-emerald-500/30'
                      }`}
                    >
                      <div className="flex justify-between items-start">
                        <div>
                          <p className="font-bold text-white">{trato.cliente_nombre}</p>
                          <p className="text-sm text-zinc-400">{trato.lotes_info}</p>
                          <p className="text-xs text-zinc-500 mt-1">{trato.fraccionamiento_nombre}</p>
                          {/* Fecha de compra para todos */}
                          {trato.fecha_compra && (
                            <p className="text-xs text-zinc-600 mt-1">Compra: {formatFecha(trato.fecha_compra)}</p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-bold text-lg text-white">{formatearMonto(trato.precio_total, trato.moneda || 'USD')}</p>
                          {trato.saldo_pendiente > 0 ? (
                            <p className="text-sm text-amber-400">Debe: {formatearMonto(trato.saldo_pendiente, trato.moneda || 'USD')}</p>
                          ) : (
                            <p className="text-sm text-emerald-400 flex items-center gap-1 justify-end">
                              <CheckCircle className="w-4 h-4" /> Liquidado
                            </p>
                          )}
                        </div>
                      </div>
                      {/* Fechas del contrato para los que deben */}
                      {trato.saldo_pendiente > 0 && (
                        <div className="mt-2 space-y-1">
                          <div className="flex justify-between text-xs text-zinc-500">
                            <span>Pagado: {formatearMonto(trato.total_pagado, trato.moneda || 'USD')}</span>
                            <span>{trato.pagos_realizados} de {trato.total_pagos} pagos</span>
                          </div>
                          {(trato.fecha_primer_pago || trato.fecha_ultimo_pago) && (
                            <div className="flex justify-between text-xs text-zinc-600">
                              <span>Inicio: {formatFecha(trato.fecha_primer_pago)}</span>
                              <span>Termina: {formatFecha(trato.fecha_ultimo_pago)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Pagos por Método */}
      <Dialog open={pagosPorMetodoDialog.open} onOpenChange={(open) => setPagosPorMetodoDialog(prev => ({ ...prev, open }))}>
        <DialogContent className="sm:max-w-2xl bg-zinc-900 border-zinc-800 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white">
              {pagosPorMetodoDialog.metodo === 'efectivo' && 'Pagos en Efectivo'}
              {pagosPorMetodoDialog.metodo === 'banco' && 'Pagos por Banco'}
              {pagosPorMetodoDialog.metodo === 'todos' && 'Todos los Pagos'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {pagosPorMetodoDialog.pagos.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No hay pagos registrados</div>
            ) : (
              [...pagosPorMetodoDialog.pagos]
                .sort((a, b) => {
                  const fechaA = a.fecha_pago ? new Date(a.fecha_pago) : new Date(0);
                  const fechaB = b.fecha_pago ? new Date(b.fecha_pago) : new Date(0);
                  return fechaB - fechaA; // Más reciente primero
                })
                .map((pago, idx) => (
                <div key={idx} className="bg-zinc-800 rounded-lg p-3">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-white font-bold">{pago.cliente_nombre || 'Cliente'}</p>
                      <p className="text-sm text-zinc-400">{pago.lotes_info}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-xs px-2 py-0.5 rounded ${pago.tipo_pago === 'Enganche' ? 'bg-amber-500/20 text-amber-400' : 'bg-blue-500/20 text-blue-400'}`}>
                          {pago.tipo_pago}
                        </span>
                        {pago.moneda_pago && (
                          <span className={`text-xs px-2 py-0.5 rounded ${pago.moneda_pago === 'MXN' ? 'bg-green-500/20 text-green-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                            {pago.moneda_pago === 'MXN' ? '🇲🇽 MXN' : '🇺🇸 USD'}
                          </span>
                        )}
                        <span className="text-xs text-zinc-500">
                          {pago.fecha_pago ? new Date(pago.fecha_pago).toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' }) : ''}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-emerald-400 font-mono">
                        {pago.moneda_pago === 'MXN' ? 'MXN ' : ''}${(pago.monto_pagado || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                      </p>
                      {pago.tipo_cambio && pago.moneda_pago === 'MXN' && (
                        <p className="text-xs text-zinc-500">TC: {pago.tipo_cambio}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
            {pagosPorMetodoDialog.pagos.length > 0 && (
              <div className="border-t border-zinc-700 pt-3 mt-3 flex justify-between">
                <span className="text-zinc-400 font-medium">Total ({pagosPorMetodoDialog.pagos.length} pagos):</span>
                <span className="text-2xl font-bold text-amber-400 font-mono">
                  ${pagosPorMetodoDialog.pagos.reduce((sum, p) => sum + (p.monto_pagado || 0), 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Enganches Cobrados */}
      <Dialog open={enganchesDialogOpen} onOpenChange={setEnganchesDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-zinc-900 border-zinc-800 text-white max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <TrendingUp className="w-5 h-5 text-emerald-500" />
              </div>
              Enganches Cobrados
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            {enganchesData.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">No hay enganches registrados</div>
            ) : (
              enganchesData.map((trato, idx) => (
                <div key={idx} className="bg-zinc-800 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <p className="text-white font-bold">{trato.cliente_nombre || 'Cliente'}</p>
                      <p className="text-sm text-zinc-400">{trato.lotes_info || `Lote ${trato.lote_numero || 'N/A'}`}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-xs px-2 py-0.5 rounded bg-amber-500/20 text-amber-400">
                          Enganche
                        </span>
                        <span className={`text-xs px-2 py-0.5 rounded ${trato.moneda === 'MXN' ? 'bg-green-500/20 text-green-400' : 'bg-emerald-500/20 text-emerald-400'}`}>
                          {trato.moneda === 'MXN' ? '🇲🇽 MXN' : '🇺🇸 USD'}
                        </span>
                        <span className="text-xs text-zinc-500">
                          Precio: {formatearMonto(trato.precio_final || 0, trato.moneda || 'USD')}
                        </span>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-xl font-bold text-emerald-400 font-mono">
                        {formatearMonto(trato.enganche || 0, trato.moneda || 'USD')}
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
            {enganchesData.length > 0 && (() => {
              // Calcular totales por moneda
              let engUSD = 0, engMXN = 0;
              enganchesData.forEach(t => {
                const m = t.moneda || 'USD';
                if (m === 'MXN') engMXN += (t.enganche || 0);
                else engUSD += (t.enganche || 0);
              });
              return (
                <div className="border-t border-zinc-700 pt-3 mt-3 flex justify-between">
                  <span className="text-zinc-400 font-medium">Total ({enganchesData.length} enganches):</span>
                  <span className="text-2xl font-bold text-emerald-400 font-mono">
                    {formatearTotalCombinado(engUSD, engMXN)}
                  </span>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de INGRESOS CONFIRMADOS - Detalle Mensual */}
      <Dialog open={ingresosDialogOpen} onOpenChange={(open) => { setIngresosDialogOpen(open); if (!open) setMesExpandido(null); }}>
        <DialogContent className="sm:max-w-3xl bg-zinc-900 border-zinc-800 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-emerald-500/20 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-emerald-500" />
                </div>
                Ingresos Confirmados - Detalle Mensual
              </div>
              {/* Filtro de Año */}
              <Select value={ingresosAnioFiltro} onValueChange={(val) => { setIngresosAnioFiltro(val); setMesExpandido(null); }}>
                <SelectTrigger className="w-28 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Año" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {aniosDisponibles.map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Resumen - Usando totales separados por moneda FILTRADO POR AÑO */}
            {(() => {
              // Filtrar pagos por año seleccionado
              const pagosFiltrados = todosPagosRealizados.filter(mes => {
                const anioMes = mes.mes?.split('-')[0];
                return anioMes === ingresosAnioFiltro;
              });
              
              // Filtrar enganches por año seleccionado
              const enganchesFiltrados = enganchesData.filter(trato => {
                const fecha = trato.fecha_inicio ? new Date(trato.fecha_inicio) : null;
                return fecha && fecha.getFullYear().toString() === ingresosAnioFiltro;
              });
              
              // Calcular mensualidades por moneda
              let mensualidadesUSD = 0, mensualidadesMXN = 0;
              pagosFiltrados.forEach(mes => {
                (mes.pagos || []).forEach(p => {
                  const moneda = p.moneda || 'USD';
                  if (moneda === 'MXN') mensualidadesMXN += (p.monto_pagado || 0);
                  else mensualidadesUSD += (p.monto_pagado || 0);
                });
              });
              
              // Calcular enganches por moneda
              let enganchesUSD = 0, enganchesMXN = 0;
              enganchesFiltrados.forEach(trato => {
                const moneda = trato.moneda || 'USD';
                const eng = trato.enganche || 0;
                if (moneda === 'MXN') enganchesMXN += eng;
                else enganchesUSD += eng;
              });
              
              const totalCobradoDisplay = calcularTotal(enganchesUSD + mensualidadesUSD, enganchesMXN + mensualidadesMXN);
              const enganchesDisplay = calcularTotal(enganchesUSD, enganchesMXN);
              const mensualidadesDisplay = calcularTotal(mensualidadesUSD, mensualidadesMXN);
              
              const simbolo = monedaDisplay === 'USD' ? 'USD $' : 'MXN $';
              const formatDisplay = (monto) => `${simbolo}${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
              
              return (
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30 text-center">
                    <p className="text-xs text-emerald-400">Total Cobrado {ingresosAnioFiltro}</p>
                    <p className="text-xl font-bold text-emerald-400 font-mono">{formatDisplay(totalCobradoDisplay)}</p>
                  </div>
                  <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700 text-center">
                    <p className="text-xs text-zinc-400">Enganches</p>
                    <p className="text-xl font-bold text-white font-mono">{formatDisplay(enganchesDisplay)}</p>
                  </div>
                  <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700 text-center">
                    <p className="text-xs text-zinc-400">Mensualidades</p>
                    <p className="text-xl font-bold text-white font-mono">{formatDisplay(mensualidadesDisplay)}</p>
                  </div>
                </div>
              );
            })()}
            
            {/* Tabla mensual */}
            <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
              <div className="grid grid-cols-5 gap-2 p-3 bg-zinc-800 text-xs font-semibold text-zinc-400 border-b border-zinc-700">
                <span>Mes</span>
                <span className="text-right">Pagos</span>
                <span className="text-right">Mensualidades</span>
                <span className="text-right">Enganches</span>
                <span className="text-right">Total</span>
              </div>
              {(() => {
                const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                
                // Filtrar pagos por año seleccionado
                const pagosFiltrados = todosPagosRealizados.filter(mes => {
                  const anioMes = mes.mes?.split('-')[0];
                  return anioMes === ingresosAnioFiltro;
                });
                
                // Filtrar enganches por año seleccionado
                const enganchesFiltrados = enganchesData.filter(trato => {
                  const fecha = trato.fecha_inicio ? new Date(trato.fecha_inicio) : null;
                  return fecha && fecha.getFullYear().toString() === ingresosAnioFiltro;
                });
                
                // Agrupar enganches por mes con moneda
                const enganchesPorMes = {};
                enganchesFiltrados.forEach(trato => {
                  if (trato.fecha_inicio) {
                    const fecha = new Date(trato.fecha_inicio);
                    const mesKey = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}`;
                    const mesNombre = `${meses[fecha.getMonth()]} ${fecha.getFullYear()}`;
                    if (!enganchesPorMes[mesKey]) {
                      enganchesPorMes[mesKey] = { mes: mesKey, mes_nombre: mesNombre, usd: 0, mxn: 0, detalle: [] };
                    }
                    const moneda = trato.moneda || 'USD';
                    if (moneda === 'MXN') enganchesPorMes[mesKey].mxn += (trato.enganche || 0);
                    else enganchesPorMes[mesKey].usd += (trato.enganche || 0);
                    enganchesPorMes[mesKey].detalle.push(trato);
                  }
                });
                
                // Combinar pagos realizados con enganches
                const mesesCombinados = {};
                
                // Agregar pagos realizados (separando por moneda)
                pagosFiltrados.forEach(mes => {
                  let mensUSD = 0, mensMXN = 0;
                  (mes.pagos || []).forEach(p => {
                    const moneda = p.moneda || 'USD';
                    if (moneda === 'MXN') mensMXN += (p.monto_pagado || 0);
                    else mensUSD += (p.monto_pagado || 0);
                  });
                  mesesCombinados[mes.mes] = {
                    mes: mes.mes,
                    mes_nombre: mes.mes_nombre,
                    pagos: (mes.pagos || []).length,
                    pagos_detalle: mes.pagos || [], // Guardar detalle de pagos
                    mensualidades_usd: mensUSD,
                    mensualidades_mxn: mensMXN,
                    enganches_usd: 0,
                    enganches_mxn: 0,
                    enganches_detalle: [] // Para guardar detalle de enganches
                  };
                });
                
                // Agregar enganches
                Object.entries(enganchesPorMes).forEach(([mesKey, eng]) => {
                  if (mesesCombinados[mesKey]) {
                    mesesCombinados[mesKey].enganches_usd = eng.usd;
                    mesesCombinados[mesKey].enganches_mxn = eng.mxn;
                    mesesCombinados[mesKey].enganches_detalle = eng.detalle || [];
                  } else {
                    mesesCombinados[mesKey] = {
                      mes: mesKey,
                      mes_nombre: eng.mes_nombre,
                      pagos: 0,
                      pagos_detalle: [],
                      mensualidades_usd: 0,
                      mensualidades_mxn: 0,
                      enganches_usd: eng.usd,
                      enganches_mxn: eng.mxn,
                      enganches_detalle: eng.detalle || []
                    };
                  }
                });
                
                // Ordenar por mes
                const mesesOrdenados = Object.values(mesesCombinados).sort((a, b) => a.mes.localeCompare(b.mes));
                
                if (mesesOrdenados.length === 0) {
                  return <div className="p-6 text-center text-zinc-500">No hay ingresos registrados en {ingresosAnioFiltro}</div>;
                }
                
                return mesesOrdenados.map((mes, idx) => (
                  <div key={mes.mes}>
                    {/* Fila del mes - clickeable */}
                    <div 
                      className={`grid grid-cols-5 gap-2 p-3 text-sm cursor-pointer hover:bg-zinc-700/50 transition-colors ${idx % 2 === 0 ? 'bg-zinc-800/30' : ''} ${mesExpandido === mes.mes ? 'bg-emerald-500/10 border-l-2 border-emerald-500' : ''}`}
                      onClick={() => setMesExpandido(mesExpandido === mes.mes ? null : mes.mes)}
                    >
                      <span className="text-zinc-300 flex items-center gap-2">
                        <svg className={`w-4 h-4 transition-transform ${mesExpandido === mes.mes ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        {mes.mes_nombre}
                      </span>
                      <span className="text-right text-zinc-400">{mes.pagos}</span>
                      <span className="text-right text-emerald-400 font-mono">{formatearTotalCombinado(mes.mensualidades_usd, mes.mensualidades_mxn)}</span>
                      <span className="text-right text-amber-400 font-mono">{formatearTotalCombinado(mes.enganches_usd, mes.enganches_mxn)}</span>
                      <span className="text-right text-white font-mono font-bold">{formatearTotalCombinado(mes.mensualidades_usd + mes.enganches_usd, mes.mensualidades_mxn + mes.enganches_mxn)}</span>
                    </div>
                    
                    {/* Desglose expandido */}
                    {mesExpandido === mes.mes && (
                      <div className="bg-zinc-900/80 border-l-2 border-emerald-500/50 ml-4 p-3 space-y-3">
                        {/* Mensualidades del mes */}
                        {(mes.pagos_detalle || []).length > 0 && (
                          <div>
                            <h5 className="text-emerald-400 text-xs font-semibold mb-2 uppercase">Mensualidades ({mes.pagos_detalle.length})</h5>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {mes.pagos_detalle.map((pago, pidx) => (
                                <div key={pidx} className="flex justify-between items-center text-sm bg-zinc-800/50 rounded px-3 py-2">
                                  <div>
                                    <span className="text-white">{pago.cliente_nombre || 'Cliente'}</span>
                                    <span className="text-zinc-500 text-xs ml-2">Pago #{pago.numero_pago}</span>
                                  </div>
                                  <span className="text-emerald-400 font-mono">{formatearMonto(pago.monto_pagado, pago.moneda || 'USD')}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Enganches del mes */}
                        {(mes.enganches_detalle || []).length > 0 && (
                          <div>
                            <h5 className="text-amber-400 text-xs font-semibold mb-2 uppercase">Enganches ({mes.enganches_detalle.length})</h5>
                            <div className="space-y-1 max-h-40 overflow-y-auto">
                              {mes.enganches_detalle.map((trato, tidx) => (
                                <div key={tidx} className="flex justify-between items-center text-sm bg-zinc-800/50 rounded px-3 py-2">
                                  <div>
                                    <span className="text-white">{trato.cliente_nombre || 'Cliente'}</span>
                                    <span className="text-zinc-500 text-xs ml-2">{trato.lotes_info}</span>
                                  </div>
                                  <span className="text-amber-400 font-mono">{formatearMonto(trato.enganche, trato.moneda || 'USD')}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Si no hay detalle */}
                        {(mes.pagos_detalle || []).length === 0 && (mes.enganches_detalle || []).length === 0 && (
                          <p className="text-zinc-500 text-sm text-center py-2">No hay detalles disponibles</p>
                        )}
                      </div>
                    )}
                  </div>
                ));
              })()}
            </div>
            
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de POR COBRAR - Pagos Pendientes */}
      <Dialog open={porCobrarDialogOpen} onOpenChange={setPorCobrarDialogOpen}>
        <DialogContent className="sm:max-w-3xl bg-zinc-900 border-zinc-800 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Clock className="w-5 h-5 text-amber-500" />
              </div>
              Por Cobrar - Detalle de Pagos Pendientes
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Resumen de riesgo - Usando totales separados por moneda */}
            <div className="grid grid-cols-3 gap-3">
              <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-center">
                <p className="text-xs text-amber-400">Total Pendiente</p>
                <p className="text-xl font-bold text-amber-400 font-mono">{formatearTotalCombinado(stats?.pendiente_usd || 0, stats?.pendiente_mxn || 0)}</p>
              </div>
              <div className="p-3 bg-red-500/10 rounded-lg border border-red-500/30 text-center">
                <p className="text-xs text-red-400">En Mora</p>
                <p className="text-xl font-bold text-red-400 font-mono">{formatearTotalCombinado(stats?.deuda_atrasada_usd || 0, stats?.deuda_atrasada_mxn || 0)}</p>
              </div>
              <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700 text-center">
                <p className="text-xs text-zinc-400">Próximos 7 días</p>
                <p className="text-xl font-bold text-white font-mono">{formatearTotalCombinado(stats?.proximos_7_dias_usd || 0, stats?.proximos_7_dias_mxn || 0)}</p>
              </div>
            </div>
            
            {/* Pagos atrasados */}
            {(stats?.pagos_atrasados || []).length > 0 && (
              <div>
                <h4 className="text-red-400 font-semibold mb-2 flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" /> Pagos Atrasados ({(stats?.pagos_atrasados || []).length})
                </h4>
                <div className="space-y-2">
                  {(stats?.pagos_atrasados || []).slice(0, 10).map((pago, idx) => (
                    <div key={idx} className="bg-red-500/10 rounded-lg p-3 border border-red-500/20">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white font-medium">{pago.cliente || 'Cliente'}</p>
                          <p className="text-xs text-zinc-400">Vencido: {pago.fecha}</p>
                        </div>
                        <p className="text-red-400 font-mono font-bold">{formatearMonto(pago.monto, pago.moneda || 'USD')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Pagos próximos */}
            {(stats?.pagos_proximos_7_dias || []).length > 0 && (
              <div>
                <h4 className="text-amber-400 font-semibold mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4" /> Próximos 7 días ({(stats?.pagos_proximos_7_dias || []).length})
                </h4>
                <div className="space-y-2">
                  {(stats?.pagos_proximos_7_dias || []).slice(0, 10).map((pago, idx) => (
                    <div key={idx} className="bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">
                      <div className="flex justify-between items-center">
                        <div>
                          <p className="text-white font-medium">{pago.cliente || 'Cliente'}</p>
                          <p className="text-xs text-zinc-400">Vence: {pago.fecha}</p>
                        </div>
                        <p className="text-amber-400 font-mono font-bold">{formatearMonto(pago.monto, pago.moneda || 'USD')}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {(stats?.pagos_atrasados || []).length === 0 && (stats?.pagos_proximos_7_dias || []).length === 0 && (
              <div className="p-6 text-center text-zinc-500">No hay pagos pendientes próximos</div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de TRATOS POR MONEDA */}
      <Dialog open={tratosMonedaDialogOpen} onOpenChange={setTratosMonedaDialogOpen}>
        <DialogContent className="sm:max-w-3xl bg-zinc-900 border-zinc-800 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <span className="text-2xl">{tratosMonedaFiltro === 'USD' ? '🇺🇸' : '🇲🇽'}</span>
              Tratos en {tratosMonedaFiltro}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Resumen */}
            {(() => {
              const totalPrecio = tratosPorMoneda.reduce((sum, t) => sum + (t.precio_total || 0), 0);
              const totalPagado = tratosPorMoneda.reduce((sum, t) => sum + (t.total_pagado || 0), 0);
              const totalPendiente = tratosPorMoneda.reduce((sum, t) => sum + (t.saldo_pendiente || 0), 0);
              const simbolo = tratosMonedaFiltro === 'USD' ? 'USD $' : 'MXN $';
              const formatNum = (n) => `${simbolo}${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
              
              return (
                <div className="grid grid-cols-4 gap-3">
                  <div className="p-3 bg-zinc-800 rounded-lg border border-zinc-700 text-center">
                    <p className="text-xs text-zinc-400">Tratos</p>
                    <p className="text-xl font-bold text-white font-mono">{tratosPorMoneda.length}</p>
                  </div>
                  <div className={`p-3 rounded-lg border text-center ${tratosMonedaFiltro === 'USD' ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-amber-500/10 border-amber-500/30'}`}>
                    <p className={`text-xs ${tratosMonedaFiltro === 'USD' ? 'text-emerald-400' : 'text-amber-400'}`}>Total Vendido</p>
                    <p className={`text-lg font-bold font-mono ${tratosMonedaFiltro === 'USD' ? 'text-emerald-400' : 'text-amber-400'}`}>{formatNum(totalPrecio)}</p>
                  </div>
                  <div className="p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30 text-center">
                    <p className="text-xs text-emerald-400">Cobrado</p>
                    <p className="text-lg font-bold text-emerald-400 font-mono">{formatNum(totalPagado)}</p>
                  </div>
                  <div className="p-3 bg-amber-500/10 rounded-lg border border-amber-500/30 text-center">
                    <p className="text-xs text-amber-400">Pendiente</p>
                    <p className="text-lg font-bold text-amber-400 font-mono">{formatNum(totalPendiente)}</p>
                  </div>
                </div>
              );
            })()}
            
            {/* Lista de tratos */}
            <div className="space-y-3">
              {tratosPorMoneda.length === 0 ? (
                <div className="text-center py-8 text-zinc-500">
                  <p>No hay tratos en {tratosMonedaFiltro}</p>
                </div>
              ) : (
                tratosPorMoneda.map((trato, idx) => {
                  const simbolo = tratosMonedaFiltro === 'USD' ? 'USD $' : 'MXN $';
                  const formatNum = (n) => `${simbolo}${(n || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
                  const pagado = trato.saldo_pendiente === 0;
                  
                  return (
                    <div key={idx} className={`p-4 rounded-lg border ${pagado ? 'bg-emerald-500/10 border-emerald-500/30' : 'bg-zinc-800 border-zinc-700'}`}>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <User className={`w-10 h-10 p-2 rounded-full ${tratosMonedaFiltro === 'USD' ? 'text-emerald-500 bg-emerald-500/20' : 'text-amber-500 bg-amber-500/20'}`} />
                          <div>
                            <p className="font-bold text-white">{trato.cliente_nombre || 'Cliente'}</p>
                            <p className="text-sm text-zinc-400">{trato.lotes_info || 'Sin lotes'}</p>
                            <p className="text-xs text-zinc-500">{trato.fraccionamiento_nombre}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className={`font-mono font-bold text-lg ${tratosMonedaFiltro === 'USD' ? 'text-emerald-400' : 'text-amber-400'}`}>
                            {formatNum(trato.precio_total)}
                          </p>
                          {pagado ? (
                            <p className="text-sm text-emerald-400 flex items-center gap-1 justify-end">
                              <CheckCircle className="w-4 h-4" /> Liquidado
                            </p>
                          ) : (
                            <p className="text-sm text-amber-400">Debe: {formatNum(trato.saldo_pendiente)}</p>
                          )}
                        </div>
                      </div>
                      {!pagado && (
                        <div className="mt-2 pt-2 border-t border-zinc-700">
                          <div className="flex justify-between text-xs text-zinc-500">
                            <span>Pagado: {formatNum(trato.total_pagado)}</span>
                            <span>{trato.pagos_realizados} de {trato.total_pagos} pagos</span>
                          </div>
                          {/* Barra de progreso */}
                          <div className="mt-2 h-2 bg-zinc-700 rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${tratosMonedaFiltro === 'USD' ? 'bg-emerald-500' : 'bg-amber-500'}`}
                              style={{ width: `${trato.precio_total > 0 ? (trato.total_pagado / trato.precio_total) * 100 : 0}%` }}
                            ></div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de PROYECCIÓN - Detalle Mensual */}
      <Dialog open={proyeccionDialogOpen} onOpenChange={setProyeccionDialogOpen}>
        <DialogContent className="sm:max-w-2xl bg-zinc-900 border-zinc-800 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <div className="p-2 bg-zinc-700 rounded-lg">
                <Calendar className="w-5 h-5 text-zinc-300" />
              </div>
              Proyección {filtroAnio}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Tabla: Mes, Enganches (cobrados + pendientes), Mensualidades (cobradas + pendientes) */}
            <div className="bg-zinc-800/50 rounded-lg overflow-hidden">
              <div className="grid grid-cols-3 gap-2 p-3 bg-zinc-800 text-xs font-semibold text-zinc-400 border-b border-zinc-700">
                <span>Mes</span>
                <span className="text-right">Enganches</span>
                <span className="text-right">Mensualidades</span>
              </div>
              {(() => {
                const meses = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
                
                // 1. Enganches COBRADOS por mes (de enganchesData) - separados por moneda
                const enganchesCobradosPorMes = {};
                enganchesData.forEach(trato => {
                  const fechaStr = trato.fecha_inicio || trato.created_at;
                  if (fechaStr) {
                    const fecha = new Date(fechaStr);
                    if (!isNaN(fecha.getTime()) && fecha.getFullYear().toString() === filtroAnio) {
                      const mesIdx = fecha.getMonth();
                      const mesNombre = meses[mesIdx];
                      if (!enganchesCobradosPorMes[mesNombre]) {
                        enganchesCobradosPorMes[mesNombre] = { usd: 0, mxn: 0 };
                      }
                      const moneda = trato.moneda || 'USD';
                      if (moneda === 'MXN') enganchesCobradosPorMes[mesNombre].mxn += (trato.enganche || 0);
                      else enganchesCobradosPorMes[mesNombre].usd += (trato.enganche || 0);
                    }
                  }
                });
                
                // 2. Mensualidades COBRADAS por mes (de pagosRealizadosPorMes) - separados por moneda
                const mensualidadesCobradosPorMes = {};
                pagosRealizadosPorMes.forEach(mes => {
                  const mesNombre = (mes.mes_nombre || '').split(' ')[0];
                  const mesCompleto = meses.find(m => m.startsWith(mesNombre) || m.substring(0,3) === mesNombre);
                  if (mesCompleto) {
                    let usd = 0, mxn = 0;
                    (mes.pagos || []).forEach(p => {
                      const moneda = p.moneda || 'USD';
                      if (moneda === 'MXN') mxn += (p.monto_pagado || 0);
                      else usd += (p.monto_pagado || 0);
                    });
                    mensualidadesCobradosPorMes[mesCompleto] = { usd, mxn };
                  }
                });
                
                // 3. Pagos PENDIENTES por mes (de proyeccionIngresos) - ya vienen separados
                const pendientesPorMes = {};
                proyeccionIngresos.forEach(mes => {
                  const mesNombre = (mes.mes_nombre || '').split(' ')[0];
                  const mesCompleto = meses.find(m => m.startsWith(mesNombre) || m.substring(0,3) === mesNombre);
                  if (mesCompleto) {
                    pendientesPorMes[mesCompleto] = {
                      enganches_usd: mes.monto_enganches_usd || 0,
                      enganches_mxn: mes.monto_enganches_mxn || 0,
                      mensualidades_usd: mes.monto_mensualidades_usd || 0,
                      mensualidades_mxn: mes.monto_mensualidades_mxn || 0
                    };
                  }
                });
                
                return meses.map((mes, idx) => {
                  // Sumar cobrados + pendientes POR MONEDA
                  const engCobradosUSD = enganchesCobradosPorMes[mes]?.usd || 0;
                  const engCobradosMXN = enganchesCobradosPorMes[mes]?.mxn || 0;
                  const engPendientesUSD = pendientesPorMes[mes]?.enganches_usd || 0;
                  const engPendientesMXN = pendientesPorMes[mes]?.enganches_mxn || 0;
                  
                  const mensCobradosUSD = mensualidadesCobradosPorMes[mes]?.usd || 0;
                  const mensCobradosMXN = mensualidadesCobradosPorMes[mes]?.mxn || 0;
                  const mensPendientesUSD = pendientesPorMes[mes]?.mensualidades_usd || 0;
                  const mensPendientesMXN = pendientesPorMes[mes]?.mensualidades_mxn || 0;
                  
                  // Total enganches y mensualidades del mes (convertidos según moneda seleccionada)
                  const totalEnganchesMes = calcularTotal(engCobradosUSD + engPendientesUSD, engCobradosMXN + engPendientesMXN);
                  const totalMensualidadesMes = calcularTotal(mensCobradosUSD + mensPendientesUSD, mensCobradosMXN + mensPendientesMXN);
                  
                  const tieneDatos = totalEnganchesMes > 0 || totalMensualidadesMes > 0;
                  const simbolo = monedaDisplay === 'USD' ? 'USD $' : 'MXN $';
                  const formatDisplay = (monto) => `${simbolo}${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
                  
                  return (
                    <div key={mes} className={`grid grid-cols-3 gap-2 p-3 text-sm ${idx % 2 === 0 ? 'bg-zinc-800/30' : ''} ${tieneDatos ? '' : 'opacity-50'}`}>
                      <span className="text-zinc-300">{mes}</span>
                      <span className={`text-right font-mono ${totalEnganchesMes > 0 ? 'text-amber-400' : 'text-zinc-600'}`}>
                        {formatDisplay(totalEnganchesMes)}
                      </span>
                      <span className={`text-right font-mono ${totalMensualidadesMes > 0 ? 'text-emerald-400' : 'text-zinc-600'}`}>
                        {formatDisplay(totalMensualidadesMes)}
                      </span>
                    </div>
                  );
                });
              })()}
              
              {/* Fila de totales */}
              {(() => {
                // Calcular totales de enganches cobrados del año POR MONEDA
                let engCobradosUSD = 0, engCobradosMXN = 0;
                enganchesData.forEach(trato => {
                  const fechaStr = trato.fecha_inicio || trato.created_at;
                  if (fechaStr) {
                    const fecha = new Date(fechaStr);
                    if (!isNaN(fecha.getTime()) && fecha.getFullYear().toString() === filtroAnio) {
                      const moneda = trato.moneda || 'USD';
                      if (moneda === 'MXN') engCobradosMXN += (trato.enganche || 0);
                      else engCobradosUSD += (trato.enganche || 0);
                    }
                  }
                });
                
                // Calcular total mensualidades cobradas del año POR MONEDA
                let mensCobradosUSD = 0, mensCobradosMXN = 0;
                pagosRealizadosPorMes.forEach(mes => {
                  (mes.pagos || []).forEach(p => {
                    const moneda = p.moneda || 'USD';
                    if (moneda === 'MXN') mensCobradosMXN += (p.monto_pagado || 0);
                    else mensCobradosUSD += (p.monto_pagado || 0);
                  });
                });
                
                // Calcular pendientes POR MONEDA
                let engPendientesUSD = 0, engPendientesMXN = 0;
                let mensPendientesUSD = 0, mensPendientesMXN = 0;
                proyeccionIngresos.forEach(m => {
                  engPendientesUSD += (m.monto_enganches_usd || 0);
                  engPendientesMXN += (m.monto_enganches_mxn || 0);
                  mensPendientesUSD += (m.monto_mensualidades_usd || 0);
                  mensPendientesMXN += (m.monto_mensualidades_mxn || 0);
                });
                
                const totalEnganches = calcularTotal(engCobradosUSD + engPendientesUSD, engCobradosMXN + engPendientesMXN);
                const totalMensualidades = calcularTotal(mensCobradosUSD + mensPendientesUSD, mensCobradosMXN + mensPendientesMXN);
                
                const simbolo = monedaDisplay === 'USD' ? 'USD $' : 'MXN $';
                const formatDisplay = (monto) => `${simbolo}${(monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;
                
                return (
                  <div className="grid grid-cols-3 gap-2 p-3 bg-zinc-900 text-sm font-bold border-t border-zinc-600">
                    <span className="text-white">TOTALES</span>
                    <span className="text-right text-amber-400 font-mono">
                      {formatDisplay(totalEnganches)}
                    </span>
                    <span className="text-right text-emerald-400 font-mono">
                      {formatDisplay(totalMensualidades)}
                    </span>
                  </div>
                );
              })()}
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal de Control de Pagarés */}
      <Dialog open={pagaresDialogOpen} onOpenChange={setPagaresDialogOpen}>
        <DialogContent className="sm:max-w-3xl bg-zinc-900 border-zinc-800 text-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <FileText className="w-5 h-5 text-orange-500" />
              Control de Pagarés
              {selectedFraccId !== 'todos' && (
                <Badge className="bg-cyan-500/20 text-cyan-400 ml-2">
                  {fraccionamientos.find(f => f.fraccionamiento_id === selectedFraccId)?.nombre || selectedFraccId}
                </Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {/* Resumen rápido */}
          <div className="grid grid-cols-3 gap-4 mb-4">
            <div className="text-center p-3 bg-zinc-800 rounded-lg">
              <p className="text-2xl font-bold text-white">{pagaresData.total_pagados || 0}</p>
              <p className="text-xs text-zinc-400">Total Pagos</p>
            </div>
            <div className="text-center p-3 bg-emerald-500/10 rounded-lg border border-emerald-500/30">
              <p className="text-2xl font-bold text-emerald-400">{pagaresData.entregados_count || 0}</p>
              <p className="text-xs text-emerald-400">Entregados</p>
            </div>
            <div className="text-center p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
              <p className="text-2xl font-bold text-orange-400">{pagaresData.pendientes_count || 0}</p>
              <p className="text-xs text-orange-400">Pendientes</p>
            </div>
          </div>
          
          {/* Nota sobre el filtro */}
          {selectedFraccId !== 'todos' && (
            <div className="mb-4 p-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg">
              <p className="text-xs text-cyan-400 flex items-center gap-2">
                <Filter className="w-4 h-4" />
                Filtrado por: <span className="font-semibold">{fraccionamientos.find(f => f.fraccionamiento_id === selectedFraccId)?.nombre}</span>
                <span className="text-zinc-400">- Cambia el filtro arriba del Dashboard para ver otros fraccionamientos</span>
              </p>
            </div>
          )}
          
          {/* Tabs */}
          <div className="flex gap-2 mb-4">
            <Button 
              size="sm" 
              variant={pagaresFiltroPendiente === 'pendientes' ? 'default' : 'outline'}
              onClick={() => setPagaresFiltroPendiente('pendientes')}
              className={pagaresFiltroPendiente === 'pendientes' ? 'bg-orange-600' : 'border-zinc-700 text-zinc-400'}
            >
              <Clock className="w-4 h-4 mr-1" />
              Pendientes ({pagaresData.pendientes_count || 0})
            </Button>
            <Button 
              size="sm" 
              variant={pagaresFiltroPendiente === 'entregados' ? 'default' : 'outline'}
              onClick={() => setPagaresFiltroPendiente('entregados')}
              className={pagaresFiltroPendiente === 'entregados' ? 'bg-emerald-600' : 'border-zinc-700 text-zinc-400'}
            >
              <CheckCircle className="w-4 h-4 mr-1" />
              Entregados ({pagaresData.entregados_count || 0})
            </Button>
          </div>
          
          {/* Lista de pagarés */}
          <div className="space-y-3 max-h-[50vh] overflow-y-auto">
            {pagaresFiltroPendiente === 'pendientes' ? (
              // Mostrar agrupados por cliente
              (pagaresData.pendientes_por_cliente || []).length > 0 ? (
                pagaresData.pendientes_por_cliente.map((cliente, cidx) => (
                  <div key={cidx} className="bg-zinc-800 rounded-lg border border-orange-500/30 overflow-hidden">
                    {/* Header del cliente */}
                    <div className="p-4 bg-orange-500/10 border-b border-orange-500/20 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-orange-500/20 flex items-center justify-center">
                          <User className="w-5 h-5 text-orange-400" />
                        </div>
                        <div>
                          <p className="font-bold text-white">{cliente.cliente_nombre}</p>
                          <p className="text-xs text-cyan-400">{cliente.fraccionamiento_nombre || 'Sin fraccionamiento'}</p>
                          {cliente.cliente_telefono && (
                            <p className="text-xs text-zinc-400 flex items-center gap-1">
                              <Phone className="w-3 h-3" /> {cliente.cliente_telefono}
                            </p>
                          )}
                        </div>
                      </div>
                      <Badge className="bg-orange-500/20 text-orange-400">
                        {cliente.pagares?.length || 0} pagaré(s) pendiente(s)
                      </Badge>
                    </div>
                    {/* Lista de pagarés del cliente */}
                    <div className="p-3 space-y-2">
                      {(cliente.pagares || []).map((pago, pidx) => (
                        <div key={pidx} className="flex items-center justify-between p-2 bg-zinc-900/50 rounded">
                          <div className="flex items-center gap-3">
                            <span className="text-orange-400 text-lg">📄</span>
                            <div>
                              <p className="text-sm text-white">
                                {pago.numero_pago === 0 ? 'Enganche' : `Mensualidad #${pago.numero_pago}`}
                              </p>
                              <p className="text-xs text-zinc-500">{pago.lote_info}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-emerald-400">{formatearMonto(pago.monto_pagado, pago.moneda)}</p>
                            <p className="text-xs text-zinc-500">
                              {pago.fecha_pago ? new Date(pago.fecha_pago).toLocaleDateString('es-MX', { day: 'numeric', month: 'short' }) : '-'}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <CheckCircle className="w-16 h-16 mx-auto mb-4 text-emerald-500/50" />
                  <p className="text-lg font-semibold text-emerald-400">¡Todos los pagarés están entregados!</p>
                  <p className="text-sm">No hay pagarés pendientes por entregar{selectedFraccId !== 'todos' ? ' en este fraccionamiento' : ''}.</p>
                </div>
              )
            ) : (
              // Mostrar entregados
              (pagaresData.entregados || []).length > 0 ? (
                pagaresData.entregados.map((pago, idx) => (
                  <div key={idx} className="flex items-center justify-between p-4 bg-zinc-800 rounded-lg border border-emerald-500/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
                        <CheckCircle className="w-5 h-5 text-emerald-400" />
                      </div>
                      <div>
                        <p className="font-bold text-white">{pago.cliente_nombre}</p>
                        <p className="text-sm text-zinc-400">
                          {pago.numero_pago === 0 ? 'Enganche' : `Mensualidad #${pago.numero_pago}`} - {pago.lote_info}
                        </p>
                        <p className="text-xs text-cyan-400">{pago.fraccionamiento_nombre}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-mono text-emerald-400">{formatearMonto(pago.monto_pagado, pago.moneda)}</p>
                      <p className="text-xs text-zinc-500">
                        Entregado: {pago.pagare_fecha ? new Date(pago.pagare_fecha).toLocaleDateString('es-MX') : '-'}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-12 text-zinc-500">
                  <FileText className="w-16 h-16 mx-auto mb-4 opacity-30" />
                  <p>No hay pagarés entregados{selectedFraccId !== 'todos' ? ' en este fraccionamiento' : ''}</p>
                </div>
              )
            )}
          </div>
          
          {/* Nota informativa */}
          <div className="mt-4 p-3 bg-zinc-800 rounded-lg border border-zinc-700">
            <p className="text-xs text-zinc-400 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              Para marcar un pagaré como entregado, ve a <span className="text-amber-400 font-semibold">Pagos</span> → selecciona el cliente → clic en el botón <span className="text-orange-400 font-semibold">"Pagaré"</span> de cada pago.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
